import { useState } from "react";
import { X, Check, ChevronRight } from "lucide-react";
import { Btn } from "../../components/shared/Btn";
import { Card } from "../../components/shared/Card";
import { FileAttachButton, AttachmentPill } from "../../components/shared/FileAttach";
import { mediaApi } from "../../lib/mediaApi";

/* Two-step wizard for adding (or editing) a sales person.
   Step 1: Personal Details (14 fields including same-as-permanent address toggle)
   Step 2: Document Upload (Aadhar / PAN / Education) — accepts images and PDFs
   On submit, assembles a sales-person object compatible with the card layout. */
export function AddSalesPersonWizard({ initial, onClose, onSubmit }) {
  const isEditing = !!initial;
  const [step, setStep] = useState(1);
  // Initial form state — hydrate from initial.profile + top-level fields when editing.
  const [form, setForm] = useState(() => {
    const p = initial?.profile || {};
    const docs = initial?.documents || {};
    const samePermCurrent =
      !!p.permanentAddress && p.permanentAddress === p.currentAddress;
    return {
      name: initial?.name || "",
      fathersName: p.fathersName || "",
      dob: p.dob || "",
      phone: initial?.phone || "",
      email: initial?.email || "",
      emergencyContact: p.emergencyContact || "",
      gender: p.gender || "",
      aadharNumber: p.aadharNumber || "",
      panNumber: p.panNumber || "",
      employeeCodeId: p.employeeCodeId || "",
      department: p.department || "Sales",
      designation: p.designation || "",
      joiningDate: p.joiningDate || new Date().toISOString().split("T")[0],
      salary: p.salary != null ? String(p.salary) : "",
      permanentAddress: p.permanentAddress || "",
      currentAddress: p.currentAddress || "",
      sameAsPermanent: samePermCurrent,
      aadharDoc: docs.aadhar || null,
      panDoc: docs.pan || null,
      educationDoc: docs.education || null,
    };
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Same-as-permanent: ticking copies permanent into current and locks the
  // current field; unticking unlocks it. Permanent changes propagate while
  // the box is ticked.
  const onToggleSameAs = (checked) => {
    setForm((f) => ({
      ...f,
      sameAsPermanent: checked,
      currentAddress: checked ? f.permanentAddress : f.currentAddress,
    }));
  };
  const setPermanent = (v) =>
    setForm((f) => ({
      ...f,
      permanentAddress: v,
      currentAddress: f.sameAsPermanent ? v : f.currentAddress,
    }));

  // Step 1 required: name, phone, joining date, gender.
  const step1Valid =
    form.name.trim() && form.phone.trim() && form.joiningDate && form.gender;

  const inputCls = "w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 bg-white focus:outline-none focus:ring-blue-500";
  const labelCls = "text-xs font-medium text-slate-600 mb-1 block";
  const fieldLabel = (s, required = false) => (
    <span className={labelCls}>
      {s}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </span>
  );

  const [docBusy, setDocBusy] = useState(null);

  // Upload to R2 on pick and store the URL, mirroring MediaStep for property
  // images. Previously the file was base64'd into `documents` and PATCHed
  // inline, which failed with 413 and would have re-sent megabytes on every
  // subsequent read of the employee record.
  const handleFile = (key) => async (file) => {
    if (!file) return;
    setDocBusy(key);
    try {
      const res = await mediaApi.uploadImage(file);
      set(key, { name: file.name, type: file.type, url: res.url });
    } catch (err) {
      alert(err?.message || "Upload failed");
    } finally {
      setDocBusy(null);
    }
  };

  const submit = () => {
    const id = initial?.id || `sp_${Date.now()}`;
    const record = {
      id,
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      /* true, not 0. In the original mock `active` was a LEAD COUNT, and
         the zero default survived the move to a real boolean flag (see the
         note in employeesApi.js). A new employee was therefore created with
         active: false -- no card in All Team, and a retry hitting "already
         exists" because the row was there all along, just invisible. */
      active: initial?.active ?? true,
      response: initial?.response ?? "—",
      fromWizard: !isEditing ? true : initial?.fromWizard,
      profile: {
        fathersName: form.fathersName.trim(),
        dob: form.dob,
        emergencyContact: form.emergencyContact.trim(),
        gender: form.gender,
        aadharNumber: form.aadharNumber.trim(),
        panNumber: form.panNumber.trim(),
        employeeCodeId: form.employeeCodeId.trim(),
        department: form.department.trim(),
        designation: form.designation.trim(),
        joiningDate: form.joiningDate,
        salary: form.salary ? parseFloat(form.salary) : null,
        permanentAddress: form.permanentAddress.trim(),
        currentAddress: form.currentAddress.trim(),
      },
      documents: {
        aadhar: form.aadharDoc,
        pan: form.panDoc,
        education: form.educationDoc,
      },
    };
    onSubmit(record, !isEditing);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-start justify-center p-4 sm:p-6 overflow-y-auto">
      <div className="w-full max-w-3xl mt-4 sm:mt-12">
        <Card className="p-0">
          {/* Wizard header */}
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-slate-900">
                {isEditing ? "Edit Employee" : "Add Employee"}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Step {step} of 2 &middot;{" "}
                {step === 1 ? "Personal Details" : "Document Upload"}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-700"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {/* Step indicator strip */}
          <div className="px-5 pt-4 pb-1 flex items-center gap-2">
            <button
              onClick={() => setStep(1)}
              className={`flex-1 text-xs font-semibold px-3 py-2 rounded-lg ${
                step === 1
                  ? "bg-[#162a52] text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              1. Personal Details
            </button>
            <button
              onClick={() => step1Valid && setStep(2)}
              disabled={!step1Valid}
              className={`flex-1 text-xs font-semibold px-3 py-2 rounded-lg ${
                step === 2
                  ? "bg-[#162a52] text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
              }`}
            >
              2. Document Upload
            </button>
          </div>

          <div className="p-5">
            {step === 1 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  {fieldLabel("Name", true)}
                  <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name" className={inputCls} autoFocus />
                </div>
                <div>
                  {fieldLabel("Father's Name")}
                  <input value={form.fathersName} onChange={(e) => set("fathersName", e.target.value)} placeholder="Father's full name" className={inputCls} />
                </div>
                <div>
                  {fieldLabel("D.O.B")}
                  <input type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} className={inputCls + " tabular-nums"} />
                </div>
                <div>
                  {fieldLabel("Phone", true)}
                  <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="10-digit mobile" className={inputCls + " tabular-nums"} />
                </div>
                <div>
                  {fieldLabel("Email")}
                  <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="name@example.com" className={inputCls} />
                </div>
                <div>
                  {fieldLabel("Emergency Contact Number")}
                  <input value={form.emergencyContact} onChange={(e) => set("emergencyContact", e.target.value)} placeholder="10-digit mobile" className={inputCls + " tabular-nums"} />
                </div>
                <div>
                  {fieldLabel("Gender", true)}
                  <select value={form.gender} onChange={(e) => set("gender", e.target.value)} className={inputCls}>
                    <option value="">Select…</option>
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  {fieldLabel("Aadhar Number")}
                  <input value={form.aadharNumber} onChange={(e) => set("aadharNumber", e.target.value)} placeholder="12 digits" className={inputCls + " tabular-nums"} />
                </div>
                <div>
                  {fieldLabel("PAN Number")}
                  <input value={form.panNumber} onChange={(e) => set("panNumber", e.target.value.toUpperCase())} placeholder="ABCDE1234F" className={inputCls + " uppercase tracking-wider"} />
                </div>
                <div>
                  {fieldLabel("Employee Code ID (ECI)")}
                  <input value={form.employeeCodeId} onChange={(e) => set("employeeCodeId", e.target.value)} placeholder="e.g. AG-2026-005" className={inputCls} />
                </div>
                <div>
                  {fieldLabel("Department")}
                  <select value={form.department} onChange={(e) => set("department", e.target.value)} className={inputCls}>
                    <option value="Sales">Sales</option>
                    <option value="Interior Design">Interior Design</option>
                    <option value="Operations">Operations</option>
                    <option value="Admin">Admin</option>
                  </select>
                </div>
                <div>
                  {/* Free text, not a select: job titles vary too much to
                      enumerate, and employees.designation accepts any string. */}
                  {fieldLabel("Designation")}
                  <input value={form.designation} onChange={(e) => set("designation", e.target.value)} placeholder="e.g. Senior Advisor" className={inputCls} />
                </div>
                <div>
                  {fieldLabel("Joining Date", true)}
                  <input type="date" value={form.joiningDate} onChange={(e) => set("joiningDate", e.target.value)} className={inputCls + " tabular-nums"} />
                </div>
                <div>
                  {fieldLabel("Salary (₹)")}
                  <input type="number" min="0" value={form.salary} onChange={(e) => set("salary", e.target.value)} placeholder="Monthly base" className={inputCls + " tabular-nums"} />
                </div>
                <div className="hidden sm:block" />

                <div className="sm:col-span-2">
                  {fieldLabel("Permanent Address")}
                  <textarea
                    value={form.permanentAddress}
                    onChange={(e) => setPermanent(e.target.value)}
                    rows={2}
                    placeholder="House no., street, locality, city, state, PIN"
                    className={inputCls + " resize-y"}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="flex items-center gap-2 mb-1.5 text-xs font-medium text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.sameAsPermanent}
                      onChange={(e) => onToggleSameAs(e.target.checked)}
                      className="w-4 h-4 rounded text-blue-600"
                    />
                    Current address is same as permanent
                  </label>
                  <textarea
                    value={form.currentAddress}
                    onChange={(e) => set("currentAddress", e.target.value)}
                    rows={2}
                    placeholder="House no., street, locality, city, state, PIN"
                    disabled={form.sameAsPermanent}
                    className={
                      inputCls +
                      " resize-y " +
                      (form.sameAsPermanent ? "bg-slate-50 text-slate-500 cursor-not-allowed" : "")
                    }
                  />
                </div>
              </div>
            ) : (
              /* Step 2 — Document Upload */
              <div className="space-y-4">
                {[
                  { key: "aadharDoc", label: "Aadhar", hint: "Front + back as one PDF or image" },
                  { key: "panDoc", label: "PAN", hint: "Scan or photo of the card" },
                  { key: "educationDoc", label: "Education", hint: "Highest qualification certificate" },
                ].map((d) => (
                  <div key={d.key}>
                    <label className={labelCls}>{d.label} document</label>
                    <p className="text-[10px] text-slate-400 mb-1.5">{d.hint}</p>
                    <div className="flex flex-wrap items-center gap-2 p-2 rounded-lg ring-1 ring-inset ring-slate-300 bg-white min-h-[44px]">
                      {form[d.key] && (
                        <AttachmentPill
                          a={form[d.key]}
                          onView={() => {
                            const url = form[d.key]?.dataUrl || form[d.key]?.url;
                            if (url) window.open(url, "_blank", "noopener,noreferrer");
                          }}
                          onRemove={() => set(d.key, null)}
                        />
                      )}
                      <FileAttachButton
                        label={docBusy === d.key ? "Uploading\u2026" : (form[d.key] ? "Replace" : "Attach file")}
                        accept="image/*,.pdf"
                        multiple={false}
                        raw
                        onPick={handleFile(d.key)}
                      />
                      {!form[d.key] && (
                        <span className="text-xs text-slate-400">
                          No file attached yet &mdash; PDF or image.
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                <p className="text-[10px] text-slate-400 leading-relaxed">
                  Documents are optional &mdash; you can on-board the person now and add documents later. All uploads stay on this device until you connect a backend.
                </p>
              </div>
            )}
          </div>

          {/* Sticky footer */}
          <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between flex-wrap gap-2">
            <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
            <div className="flex items-center gap-2">
              {step === 2 && (
                <Btn variant="secondary" onClick={() => setStep(1)}>Back</Btn>
              )}
              {step === 1 ? (
                <Btn variant="primary" onClick={() => setStep(2)} disabled={!step1Valid}>
                  Next<ChevronRight className="w-4 h-4" />
                </Btn>
              ) : (
                <Btn variant="primary" onClick={submit}>
                  <Check className="w-4 h-4" />
                  {isEditing ? "Save changes" : "Add Employee"}
                </Btn>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
